import _ from "lodash"
import {
  Polygon,
  MultiPolygon,
  LineString,
  Marker,
  Coordinate,
  MultiLineString,
} from "maptalks"
import turfCenter from "@turf/center"
import turfBuffer from "@turf/buffer"

import * as THREE from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader"
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader"

import { GroundLabel } from "../object3d/GroundLabel"
import { Billboard } from "../object3d/Billboard"
import { Marker3d } from "../object3d/Marker3d"
import { getCenterFromGeometry } from "./geometry"

const GeometryType = {
  Polygon,
  MultiPolygon,
}

const ORDINAL_HEIGHT = 0 // in version 3D change ORDINAL_HEIGHT for set altitude of geometry

const UNIT_Z_INDEX = 1

const getAltitude = (properties) =>
  Math.max(0, properties.ordinal * ORDINAL_HEIGHT || 0)

const createRoomUnit = (feature, style, options) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)

  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}
const createAmenityUnit = (feature, style, options = {}) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const area = new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
  return area
}

const createNonpublicUnit = (feature, style, options = {}) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: {
      ...style,
    },
  })
}
const createWalkwayUnit = (feature, style, options) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: {
      ...symbolStyle,
    },
  })
}

const createWaterFixture = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol: {
      ...style,
    },
  })
}

const createVegetationFixture = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol: {
      ...symbolStyle,
    },
  })
}

const createObstructionalFixture = (feature, style = {}, options = {}) => {
  const { geometry, properties = {} } = feature
  const { model3d } = properties
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (!_.isEmpty(model3d)) return
  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}

const createVegetationSection = (feature, symbol = {}) => {
  const { geometry, properties } = feature
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol,
  })
}

const creatingSeatingSection = (feature, symbol = {}) => {
  const { geometry, properties } = feature
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol,
  })
}

const creatingDefaultSection = (feature, symbol = {}) => {
  const { geometry, properties } = feature
  try {
    return new GeometryType[geometry.type](geometry.coordinates, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol,
    })
  } catch (error) {
    console.warn(`Error creatingDefaultSection`, geometry.type)
  }
}

const createEatingDrinkingSection = (feature, symbol = {}) => {
  const { geometry, properties } = feature
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol,
  })
}

const createPrincipalOpening = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  try {
    return new LineString(geometry.coordinates, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol: {
        ...style,
      },
    })
  } catch (error) {
    console.log(`error creating pedestrian principal opening:`, feature)
  }
}
const createPedestrianOpening = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  try {
    return new LineString(geometry.coordinates, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol: {
        ...style,
      },
    })
  } catch (error) {
    console.log(`error creating pedestrian opening:`, feature)
  }
}

const loadModel3d = (model3d, coordinate, threeLayer) => {
  return new Promise((resolve) => {
    const loader = new GLTFLoader()
    const { url, properties: modelProperties } = model3d
    loader.load(url, (gltf) => {
      const object3d = gltf.scene
      // จัดขนาด + scale ที่เหมาะสม
      object3d.rotation.x = _.get(modelProperties, "rotation.x")
      object3d.rotation.y = _.get(modelProperties, "rotation.y")
      object3d.scale.set(...(_.get(modelProperties, "scale") || []))

      const object = threeLayer.toModel(object3d, {
        coordinate,
      })

      object.getObject3d().traverse((child) => {
        if (child.isMesh === true) {
          child.material.transparent = true
          child.material.metalness = 0.1 // set to near 0 because metal requires environment to reflect light to, if there's nothing then metal will become black.
        }
      })

      resolve(object)
    })
  })
}

const create3DModels = async (
  models,
  defaultCoordinate,
  properties,
  threeLayer
) => {
  let modelObjs = []
  for (let j = 0; j < models.length; j++) {
    const model = models[j]
    const positionCoord = _.get(model, "properties.position")
    const coord = positionCoord || defaultCoordinate
    const object = await loadModel3d(model, coord, threeLayer)
    object.properties = properties
    modelObjs.push(object)
  }

  return modelObjs
}

const createExtrudePolygon = (
  geometry,
  threeLayer,
  material,
  height,
  properties = {},
  options
) => {
  const { offset = 0 } = options
  const offsetGeometry = turfBuffer(geometry, offset, { units: "meters" })
  const object = threeLayer.toExtrudePolygon(
    offsetGeometry,
    { height },
    material
  )
  object.properties = properties
  return object
}

const create3DMarker = (
  coordinates,
  options,
  iconPath,
  pinMarkerPath,
  threeLayer,
  markerProperties
) => {
  return new Marker3d(
    coordinates,
    options,
    iconPath,
    pinMarkerPath,
    threeLayer,
    markerProperties
  )
}

export const createObject3dFromSvgPath = (svgPath, options) => {
  const { extrusion, scale, material, xOffset, yOffset } = options
  const loader = new SVGLoader()
  const svgData = loader.parse(`<svg>
          <path d="${svgPath}" />
      </svg>`)
  const svgGroup = new THREE.Group()

  svgData.paths.forEach((path) => {
    const shapes = SVGLoader.createShapes(path)
    shapes.forEach((shape) => {
      const meshGeometry = new THREE.ExtrudeBufferGeometry(shape, {
        depth: extrusion,
        bevelEnabled: false,
      })
      const mesh = new THREE.Mesh(meshGeometry, material)

      svgGroup.add(mesh)
    })
  })

  const box = new THREE.Box3().setFromObject(svgGroup)
  const size = box.getSize(new THREE.Vector3())

  const defaultYOffset = size.y / -2
  const defaultXOffset = size.x / -2

  // Offset all of group's elements, to center them
  svgGroup.children.forEach((item) => {
    item.position.x = xOffset || defaultXOffset
    item.position.y = yOffset || defaultYOffset
  })
  svgGroup.rotateX(-Math.PI / 2)
  svgGroup.scale.set(scale, scale, scale)
  return svgGroup
}

export const getExtrudeConfig = (extrudeConfigObj, featureType, category) =>
  _.get(extrudeConfigObj, `${featureType}.${category || featureType}`)

const getOccupantMarkerConfig = (feature, extrudeConfigObj) => {
  const kiosk = _.get(feature, "properties.kiosk", null)
  const unit = _.get(feature, "properties.anchor.properties.unit", null)
  const location = kiosk || unit
  const { feature_type: featureType } = location || {}

  const { category } = _.get(location, "properties") || {}

  return getExtrudeConfig(
    extrudeConfigObj,
    featureType,
    category || featureType
  )
}

export const styledFeatureGenerator = (mapTheme) => {
  const getElementSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.geometry.symbol`)
    if (featureType === key) return featureTypeTheme
    const categoryTheme = _.get(mapTheme, `${key}.geometry.symbol`)
    return _.merge({}, featureTypeTheme, categoryTheme)
  }

  const getLabelSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.label`)
    const categoryTheme = _.get(mapTheme, `${key}.label`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    let symbols = _.values(_.map(mergedSymbol, "symbol"))
    const markerIndexToMove = symbols.findIndex(
      ({ elementType }) => elementType === "label.marker"
    )

    // Move the label.marker symbol to the last of array
    if (markerIndexToMove >= 0) {
      const markerSymbolToMove = _.pullAt(symbols, markerIndexToMove)[0]
      symbols.push(markerSymbolToMove)
    }

    return symbols
  }

  const getElementOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeOptions = _.get(
      mapTheme,
      `${featureType}.geometry.options`
    )

    const categoryOptions = _.get(mapTheme, `${key}.geometry.options`)

    return _.merge({}, featureTypeOptions, categoryOptions)
  }

  const getLabelOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeSymbol = _.get(mapTheme, `${featureType}.label`)
    const categorySymbol = _.get(mapTheme, `${key}.label`)
    const mergedSymbol = _.merge({}, featureTypeSymbol, categorySymbol)
    return _.reduce(
      mergedSymbol,
      (acc, symbol) => ({ ...acc, ..._.get(symbol, "options", {}) }),
      {}
    )
  }

  const createOccupantMarker = (feature, mapConfig) => {
    const markerConfig = getOccupantMarkerConfig(feature, mapConfig)
    const markerHeight = _.get(markerConfig, "height")
    const { properties } = feature
    if (!properties.anchor) return
    const { geometry } = properties?.anchor
    const occupantName = _.isEmpty(properties.short_name)
      ? properties.name
      : properties.short_name

    // Get Label from located unit / kiosk
    const { kiosk, anchor } = properties
    const { unit } = anchor.properties
    let locatedFeature = kiosk || unit || null

    if (locatedFeature) {
      const { feature_type, properties } = locatedFeature
      const { category } = properties
      const locatedLabelOption =
        feature_type === "kiosk"
          ? getLabelOptions(`${feature_type}`)
          : getLabelOptions(`${feature_type}.${category}`)
      const { hidden = false } = locatedLabelOption || {}
      if (hidden) return
    }

    const logoUrl = _.get(properties, "logo.url")
    const renderType =
      properties.render_type === "Logo" && logoUrl
        ? properties.render_type
        : "Name"

    const labelSymbol = getLabelSymbol(`occupant-${_.toLower(renderType)}`)
    const markerSymbol = _.last(labelSymbol)
    const coordinates = _.get(geometry, "coordinates")

    switch (renderType) {
      case "Logo":
        _.set(markerSymbol, "markerFile", logoUrl)

        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties) + markerHeight,
          },
          symbol: labelSymbol,
        })

      case "Name":
      default:
        const renderPriority = properties.render_priority || 3
        const priorityLabelSymbol = getLabelSymbol(
          `occupant-${_.toLower(renderType)}-${renderPriority}`
        )
        const priorityMarkerSymbol = _.last(priorityLabelSymbol) || {}
        return new Marker(coordinates, {
          properties: {
            name: occupantName.en,
            altitude: getAltitude(properties) + markerHeight,
          },
          symbol: {
            textName: "{name}",
            //TODO:: implement symbol scaler later
            textHaloRadius: {
              stops: [
                [20, 1],
                [21, 2],
              ],
            },
            ...markerSymbol,
            ...priorityMarkerSymbol,
          },
        })
    }
  }

  const getFeatureProperties = (feature) => {
    const { id, feature_type, properties } = feature
    const { category, ordinal, style = {} } = properties
    const elementStyle = getElementSymbol(`${feature_type}.${category}`)
    const { allowOverride } = getElementOptions(`${feature_type}.${category}`)
    if (allowOverride) _.merge(elementStyle, style)
    const { polygonFill: color } = elementStyle
    const featureProperty = {
      id,
      feature_type,
      category: category || feature_type,
      ordinal,
      defaultColor: color,
    }
    return featureProperty
  }

  return {
    getHilighPolygonalSymbol: (type) => {
      switch (type) {
        case "MultiPolygon":
        case "Polygon": {
          return getElementSymbol("hilight-polygonal")
        }
        case "Point": {
          return getElementSymbol("hilight-marker")
        }
        default:
          break
      }
    },
    getHighlightMarkerSymbol: () => {
      return getElementSymbol("highlighted-marker")
    },
    createVenue: (feature) => {
      const { geometry, feature_type } = feature
      const symbolStyle = getElementSymbol(feature_type)
      return new GeometryType[geometry.type](geometry.coordinates, {
        symbol: {
          ...symbolStyle,
        },
      })
    },

    createLevel: (feature) => {
      const { geometry, properties, feature_type } = feature
      const { category } = properties

      const style = getElementSymbol(`${feature_type}.${category}`)
      // const options = getElementOptions(`${feature_type}.${category}`)

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          altitude: getAltitude(properties),
        },
        symbol: {
          ...style,
        },
      })
    },

    createUnit: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(`${feature_type}.${category}`)
      switch (feature.properties.category) {
        case "room":
        case "unenclosedarea":
        case "unspecified":
        case "recreation":
          return createRoomUnit(feature, elementStyle, options).setZIndex(
            UNIT_Z_INDEX
          )
        case "restroom":
        case "restroom.female":
        case "restroom.male":
        case "escalator":
        case "elevator":
        case "stairs":
        case "parking":
        case "smokingarea":
          return createAmenityUnit(feature, elementStyle, options).setZIndex(
            UNIT_Z_INDEX
          )
        case "nonpublic":
        case "opentobelow":
          return createNonpublicUnit(feature, elementStyle, options).setZIndex(
            UNIT_Z_INDEX
          )
        case "walkway":
        case "footbridge":
          return createWalkwayUnit(feature, elementStyle, options).setZIndex(
            UNIT_Z_INDEX - 1
          )
        default:
          break
      }
    },

    createAmenity: (feature) => {
      const { geometry, id, properties, feature_type } = feature
      const { category, style } = properties
      const labelSymbol = getLabelSymbol(`${feature_type}.${category}`)
      const { allowOverride } = getLabelOptions(`${feature_type}.${category}`)

      // If element does not have it's own style fall back to style obtained from theme
      const defaultStyle = (allowOverride ? style : labelSymbol) || labelSymbol
      return new Marker(geometry.coordinates, {
        properties: {
          id,
          feature_type: "amenity",
          category: properties.category,
          altitude: getAltitude(properties),
        },
        symbol: defaultStyle,
        defaultSymbol: defaultStyle,
      })
    },
    createMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const markerSymbol = getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          symbol: markerSymbol,
        })
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },
    createOriginMarker: (feature) => {
      const { id, geometry, properties } = feature
      const coordinates = getCenterFromGeometry(geometry)
      const markerSymbol = getElementSymbol("origin-marker")
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          id,
          symbol: markerSymbol,
        })
      } catch (error) {
        console.log(`error creating origin marker:`, feature)
      }
    },
    createDestinationMarker: (feature) => {
      const { id, geometry, properties } = feature
      const coordinates = getCenterFromGeometry(geometry)
      const markerSymbol = getElementSymbol("pin-marker")
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          id,
          symbol: markerSymbol,
        })
      } catch (error) {
        console.log(`error creating destination marker:`, feature)
      }
    },

    createUserLocationMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const symbolStyle = getElementSymbol("user-location") || {}
        const marker = new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
          },
          symbol: symbolStyle.start,
        })

        marker.animate(
          {
            symbol: symbolStyle.end,
          },
          {
            repeat: true,
            easing: "upAndDown",
            duration: 1500,
          }
        )
        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createLastUserLocationMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const symbolStyle = getElementSymbol("last-user-location") || {}
        const options = getElementOptions("last-user-location") || {}
        const marker = new Marker(coordinates, {
          properties: {
            ...options,
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
          },
          symbol: symbolStyle,
        })

        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createKiosk: (feature) => {
      const { geometry, feature_type, properties, id } = feature
      const {
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(
        `${feature_type}.${category}.geometry.options`
      )

      const { allowOverride, inheritFillColorToLine } = options

      const symbolStyle = { ...elementStyle }

      if (allowOverride) _.merge(symbolStyle, properties.style)
      if (inheritFillColorToLine)
        symbolStyle.lineColor = symbolStyle.polygonFill

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          id,
          feature_type,
          category: "kiosk",
          altitude: getAltitude(properties),
        },
        symbol: symbolStyle,
        defaultSymbol: symbolStyle,
      })
    },

    createSection: (feature) => {
      const { properties, feature_type } = feature
      const { category } = properties
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      switch (category) {
        case "eatingdrinking":
          return createEatingDrinkingSection(feature, elementStyle)
        case "vegetation":
          return createVegetationSection(feature, elementStyle)
        case "seating":
          return creatingSeatingSection(feature, elementStyle)
        default:
          return creatingDefaultSection(feature, elementStyle)
      }
    },

    createOccupant: (feature, mapConfig) => {
      return createOccupantMarker(feature, mapConfig)
    },

    createOpening: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(`${feature_type}.${category}`)
      switch (feature.properties.category) {
        case "pedestrian":
          return createPedestrianOpening(feature, elementStyle, options)
        case "pedestrian.principal":
          return createPrincipalOpening(feature, elementStyle, options)
        default:
          break
      }
    },

    createFixture: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(`${feature_type}.${category}`)
      switch (feature.properties.category) {
        case "water":
          return createWaterFixture(feature, elementStyle, options)
        case "vegetation":
          return createVegetationFixture(feature, elementStyle, options)
        case "equipment":
        case "obstruction":
        case "stage":
          return createObstructionalFixture(feature, elementStyle, options)
        default:
          break
      }
    },

    createLineStringFromGeometries: (geometries) => {
      const mergedCoordinates = _(geometries)
        .map((geometry) => {
          switch (geometry.type) {
            case "Point":
              return [
                geometry?.getCoordinates
                  ? geometry?.getCoordinates()
                  : new Coordinate(geometry.coordinates),
              ]
            case "Polygon":
              return [
                geometry?.getCenter
                  ? geometry?.getCenter()
                  : new Polygon(geometry.coordinates).getCenter(),
              ]
            case "MultiPolygon":
              return [
                geometry?.getCenter
                  ? geometry?.getCenter()
                  : new MultiPolygon(geometry.coordinates).getCenter(),
              ]
            case "LineString":
            default:
              return geometry?.getCoordinates
                ? geometry?.getCoordinates()
                : new LineString(geometry.coordinates).getCoordinates()
          }
        })
        .flatten()
        .value()
      const stepPathLineSymbol = getElementSymbol("navigation-path")
      const startPathSymbolMarker = getElementSymbol("navigation-path-start")

      const line = new LineString(mergedCoordinates, {
        smoothness: 0.5,
        symbol: [...stepPathLineSymbol, startPathSymbolMarker],
      })

      return line
    },

    createStepPath: (feature, style) => {
      const { id, geometry, properties } = feature
      const stepPathLineSymbol = getElementSymbol("navigation-path")
      const startPathSymbolMarker = getElementSymbol("navigation-path-start")
      const endPathSymbolMarker = getElementSymbol("navigation-path-end")
      const symbols = [
        ...stepPathLineSymbol,
        startPathSymbolMarker,
        endPathSymbolMarker,
      ]
      return new LineString(geometry.coordinates, {
        properties: {
          altitude: getAltitude(properties),
        },
        smoothness: 0.5,
        id,
        symbol: symbols,
      })
    },

    createDecoration: (decoration, options) => {
      const { type, coordinates } = decoration
      const { id, ordinal, symbol: inlineSymbol } = options

      const themeSymbol = getElementSymbol("decoration")
      const { allowOverride, inheritFillColorToLine } =
        getElementOptions("decoration")

      // When no theme entry exists, fall back to inline symbol unchanged.
      const resolvedSymbol = themeSymbol
        ? { lineWidth: 1.5, ...themeSymbol }
        : { lineWidth: 1.5, ...(inlineSymbol || {}) }
      if (themeSymbol && allowOverride) _.merge(resolvedSymbol, inlineSymbol)
      if (inheritFillColorToLine)
        resolvedSymbol.lineColor = resolvedSymbol.polygonFill

      const formattedProperties = {
        properties: {
          id,
          altitude: getAltitude(options),
          ordinal,
        },
        symbol: resolvedSymbol,
        defaultSymbol: resolvedSymbol,
      }
      switch (type) {
        case "Polygon":
          return new Polygon(coordinates, formattedProperties)
        case "MultiPolygon":
          return new MultiPolygon(coordinates, formattedProperties)
        case "LineString":
          return new LineString(coordinates, formattedProperties)
        case "MultiLineString":
          return new MultiLineString(coordinates, formattedProperties)
        default:
          return null
      }
    },

    /** Three JS */
    create3DFootprint: async (feature, threeLayer, options) => {
      const extrudeHeight = _.get(options, "height")
      if (!extrudeHeight) return

      const { properties } = feature
      const footprintProperties = getFeatureProperties(feature)

      const objects = []
      // Use properties.model3d if exists
      if (Array.isArray(properties.model3d) && properties.model3d.length > 0) {
        const models = properties.model3d
        const center = turfCenter(feature)
        const coordinate = _.get(center, "geometry.coordinates")
        for (const model of models) {
          const object = await loadModel3d(model, coordinate, threeLayer)
          object.properties = footprintProperties
          objects.push(object)
        }
      } else {
        const color = footprintProperties.defaultColor
        //skip create extrude polygon if color === "transparent" because three.js can't convert color:"transparent" to material color
        if (color === "transparent") return
        const material = new THREE.MeshPhongMaterial({
          color,
          transparent: true,
        })
        const object = createExtrudePolygon(
          feature.geometry,
          threeLayer,
          material,
          extrudeHeight,
          footprintProperties,
          {}
        )
        objects.push(object)
      }

      return objects
    },
    create3DGroundLabel: (label, threeLayer) => {
      const text = label.properties.name
      const bound = label.geometry.coordinates[0]
      if (_.isNil(bound)) throw new Error("Invalid coordinates")
      const { angle, fillStyle, ...properties } = label.properties

      return new GroundLabel(
        bound,
        { text, angle, fillStyle, ...properties },
        threeLayer
      )
    },
    create3DBillboard: (billboard, threeLayer) => {
      const { id, feature_type, properties } = billboard
      const { logo, altitude, scale } = properties
      const coordinates = getCenterFromGeometry(billboard.geometry)
      const billboardProperties = {
        ...billboard.properties,
        id,
        feature_type,
      }

      const options = {
        altitude,
        scale,
      }

      return new Billboard(
        coordinates,
        options,
        logo?.url,
        threeLayer,
        billboardProperties
      )
    },
    create3DAmenityMarker: (feature, threeLayer, config) => {
      const { geometry, properties, feature_type, id } = feature
      const { category, units } = properties
      const amenityLocatedUnit = _.first(units)
      const unitCategory = _.get(amenityLocatedUnit, "properties.category")
      const unitType = _.get(amenityLocatedUnit, "feature_type")
      const unitConfig = getExtrudeConfig(config, unitType, unitCategory)

      const labelSymbol = getLabelSymbol(`${feature_type}.${category}`)
      const coordinates = _.get(geometry, "coordinates")

      const { markerPath: pinMarkerPath, markerFill: pinMarkerColor } = _.get(
        labelSymbol,
        "[0]",
        {}
      )
      const { markerPath: iconPath, markerFill: iconColor } = _.get(
        labelSymbol,
        "[1]",
        {}
      )

      const markerProperties = {
        ...properties,
        id,
        feature_type,
      }

      const options = {
        scale: 0.0015,
        pinMarkerColor,
        iconColor,
        altitude: _.get(unitConfig, "height", 0),
      }

      return create3DMarker(
        coordinates,
        options,
        iconPath,
        pinMarkerPath,
        threeLayer,
        markerProperties
      )
    },
    create3DUnit: async (unit, threeLayer) => {
      const { id, feature_type, properties } = unit
      const { category, ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
        ordinal,
      }

      const center = turfCenter(unit)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },

    createVenue3DModel: async (venue, threeLayer) => {
      const { id, feature_type, properties } = venue
      const { category, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
      }
      const center = turfCenter(venue)
      const centerCoord = _.get(center, "geometry.coordinates")
      const modelPosition = _.get(model3d, "properties.position", centerCoord)
      const models = await create3DModels(
        model3d,
        modelPosition,
        modelProperty,
        threeLayer
      )
      return models
    },

    create3DFixture: async (fixture, threeLayer) => {
      const { id, feature_type, properties } = fixture
      const { category, ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
        ordinal,
      }

      const center = turfCenter(fixture)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },
    createExtrudedUnit: (unit, threeLayer, options) => {
      const extrudeHeight = _.get(options, "height")
      if (!extrudeHeight) return
      //NOTE: this is temporary condition that only extrude when the unit is "room"
      const unitProperty = getFeatureProperties(unit)
      //NOTE: offset will be dynamic in future improvement
      const options3d = { offset: -0.1 }
      const color = unitProperty.defaultColor
      if (color === "transparent") return
      const material = new THREE.MeshPhongMaterial({ color, transparent: true })
      const object = createExtrudePolygon(
        unit.geometry,
        threeLayer,
        material,
        extrudeHeight,
        unitProperty,
        options3d
      )
      return object
    },
    createExtrudedKiosk: (kiosk, threeLayer, options) => {
      const extrudeHeight = _.get(options, "height")
      if (!extrudeHeight) return
      const kioskProperty = getFeatureProperties(kiosk)
      //NOTE: offset will be dynamic in future improvement
      const options3d = { offset: -0.1 }
      const color = kioskProperty.defaultColor
      if (color === "transparent") return
      const material = new THREE.MeshPhongMaterial({ color, transparent: true })
      const object = createExtrudePolygon(
        kiosk.geometry,
        threeLayer,
        material,
        extrudeHeight,
        kioskProperty,
        options3d
      )
      return object
    },
    createAmbientLight: (config) => {
      const { color: colorString = "0xffffff", intensity = 1 } = config
      const color = parseInt(colorString, 16)
      const ambientLight = new THREE.AmbientLight(color, intensity)
      return ambientLight
    },
    createDirectionalLight: (config) => {
      const {
        color: colorString = "0xffffff",
        intensity = 1,
        position: positionString = [0, 0, 0],
      } = config
      const color = parseInt(colorString, 16)
      const [x, y, z] = positionString
      const light = new THREE.DirectionalLight(color, intensity)
      light.position.set(x, y, z).normalize()
      return light
    },
  }
}

export const getLocationIdByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "kiosk":
    case "unit":
    case "amenity":
    case "opening":
      return feature.id
    case "occupant":
      return feature.properties?.unit_id
        ? feature.properties?.unit_id
        : feature.properties?.kiosk_id
    case "promotion":
    case "event":
      return feature.properties?.feature_id
    default:
      return
  }
}
